import {
	useCallback,
	useEffect,
	useState,
	type Dispatch,
	type SetStateAction,
} from 'react';

import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import toast from '@liquidweb-dev/undercurrent/toast';

import FadeCollapse from 'components/FadeCollapse';
import { AcceptDispatchOkI } from 'modules/acceptJs';
import {
	ConfirmSavedPaymentContent,
	ConfirmNewAuthNetPaymentContent,
	ConfirmNewStripePaymentContent,
	StartContent,
} from './contents';
import dialogMakePaymentContext, {
	type DialogMakePaymentFormErrors,
	type DialogMakePaymentFormValues,
	type HeldStripePaymentI,
} from './dialogMakePaymentContext';
import StripeProvider from './StripeProvider';
import titles, { type DialogMakePaymentStep } from './titles';
import { type ResolvedPaymentMethodI } from './usePaymentMethods';

const initialFormValues: DialogMakePaymentFormValues = {
	amount: '0',
	payFullBalance: false,
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
	savePaymentMethod: false,
	paymentMode: 'saved',
	paymentInstrumentId: '',
};

const initialFormErrors: DialogMakePaymentFormErrors = {
	amount: '',
	payFullBalance: '',
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
	savePaymentMethod: '',
	paymentMode: '',
	paymentInstrumentId: '',
};

/** The values the form opens with, and the ones it returns to when it closes */
const getInitialFormValues = ({
	accountBalanceDisplay,
	defaultPaymentInstrumentId,
	initialPaymentMode,
}: {
	accountBalanceDisplay: string;
	defaultPaymentInstrumentId: string;
	initialPaymentMode: DialogMakePaymentFormValues['paymentMode'];
}): DialogMakePaymentFormValues => ({
	...initialFormValues,
	amount: accountBalanceDisplay,
	/** should be true if there is an account balance */
	payFullBalance: Number(accountBalanceDisplay) > 0,
	paymentMode: initialPaymentMode,
	paymentInstrumentId: defaultPaymentInstrumentId,
});

export interface PaymentStepsProps {
	accountBalanceDisplay: string;
	defaultPaymentInstrumentId: string;
	hasPaymentMethodsError: boolean;
	heldStripePayment: HeldStripePaymentI | null;
	/** false while a Stripe payment is in flight or its card is held uncaptured */
	isClosable: boolean;
	isPayPalAllowed: boolean;
	isStripeAllowed: boolean;
	/** whether the dialog holding these steps is open */
	open: boolean;
	paymentMethods: ResolvedPaymentMethodI[];
	onClose: () => void;
	refetchPaymentMethods: () => void;
	setHeldStripePayment: Dispatch<SetStateAction<HeldStripePaymentI | null>>;
}

/**
 * The steps of the payment, and the form state they share. Mounted only once the
 * account data has arrived, so every value it opens with is ready to be an
 * initial value - nothing to initialize later, and nothing to rearrange under
 * the user.
 */
const PaymentSteps = ({
	accountBalanceDisplay,
	defaultPaymentInstrumentId,
	hasPaymentMethodsError,
	heldStripePayment,
	isClosable,
	isPayPalAllowed,
	isStripeAllowed,
	open,
	paymentMethods,
	onClose,
	refetchPaymentMethods,
	setHeldStripePayment,
}: PaymentStepsProps) => {
	// an account with nothing on file to pay with starts on a new card - a failure
	// to fetch is not an empty wallet, and has its own say on the saved panel
	const initialPaymentMode =
		paymentMethods.length > 0 || hasPaymentMethodsError ? 'saved' : 'new';

	const [step, setStep] = useState<DialogMakePaymentStep>('start');
	const [opaqueData, setOpaqueData] = useState<
		AcceptDispatchOkI['opaqueData'] | null
	>(null);
	const [formValues, setFormValues] = useState<DialogMakePaymentFormValues>(
		() =>
			getInitialFormValues({
				accountBalanceDisplay,
				defaultPaymentInstrumentId,
				initialPaymentMode,
			}),
	);
	const [formErrors, setFormErrors] = useState(initialFormErrors);

	const [confirmationToken, setConfirmationToken] = useState<string | null>(
		null,
	);

	/** Puts the steps back to how the dialog opens. */
	const reset = useCallback(() => {
		setStep('start');
		setOpaqueData(null);
		setConfirmationToken(null);
		setFormErrors(initialFormErrors);
		setFormValues(
			getInitialFormValues({
				accountBalanceDisplay,
				defaultPaymentInstrumentId,
				initialPaymentMode,
			}),
		);
	}, [accountBalanceDisplay, defaultPaymentInstrumentId, initialPaymentMode]);

	/** The one way out of the dialog, so no close of ours can skip the reset. */
	const handleClose = useCallback(() => {
		// checked before the reset, which would wipe a payment that is being taken
		if (!isClosable) return;

		reset();
		onClose();
	}, [isClosable, reset, onClose]);

	// The backstop for the closes that are not ours: escape and a backdrop click
	// are the `Dialog`'s own, wired above these steps, so they never reach
	// `handleClose`. Resetting again after one of ours costs nothing.
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const clearErrors = () => setFormErrors(initialFormErrors);

	const clearSensitiveFormValues = useCallback(() => {
		setFormValues((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
		// also clear any errors associated with these form values
		setFormErrors((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
	}, []);

	const handleStepChange = (newStep: DialogMakePaymentStep) => {
		setStep(newStep);
		clearErrors();
	};

	const handleSubmitSuccess = () => {
		// a payment that went through holds nothing, so this close skips the guard
		reset();
		onClose();
		toast.success('Payment successful', {
			description: `Your payment of ${formatCurrency(formValues.amount)} was confirmed`,
		});
	};

	/**
	 * An error during the final submit is deliberately vague since we are not certain if the payment was in fact captured during an error.
	 */
	const handleSubmitError = () => {
		toast.danger('We couldn’t complete your payment', {
			description:
				'Check your balance before trying again, and contact support if it keeps failing.',
		});
		setStep('start');
	};

	return (
		<>
			<DialogTitle onClose={handleClose}>{titles[step]}</DialogTitle>
			<DialogContent>
				<dialogMakePaymentContext.Provider
					value={{
						accountBalanceDisplay,
						confirmationToken,
						formErrors,
						formValues,
						hasPaymentMethodsError,
						heldStripePayment,
						isPayPalAllowed,
						isStripeAllowed,
						paymentMethods,
						opaqueData,
						step,
						clearSensitiveFormValues,
						onSubmitSuccess: handleSubmitSuccess,
						onSubmitError: handleSubmitError,
						refetchPaymentMethods,
						setConfirmationToken,
						setFormErrors,
						setHeldStripePayment,
						setFormValues,
						setOpaqueData,
						setStep: handleStepChange,
					}}
				>
					<StripeProvider>
						{/* each step is unmounted while collapsed - a step that isn't on
						screen shouldn't still be holding form inputs or firing requests */}
						<FadeCollapse in={step === 'start'} unmountOnExit>
							<StartContent />
						</FadeCollapse>

						<FadeCollapse in={step === 'confirmSavedPayment'} unmountOnExit>
							<ConfirmSavedPaymentContent />
						</FadeCollapse>

						<FadeCollapse
							in={step === 'confirmNewAuthNetPayment'}
							unmountOnExit
						>
							<ConfirmNewAuthNetPaymentContent />
						</FadeCollapse>

						<FadeCollapse in={step === 'confirmNewStripePayment'} unmountOnExit>
							<ConfirmNewStripePaymentContent />
						</FadeCollapse>
					</StripeProvider>
				</dialogMakePaymentContext.Provider>
			</DialogContent>
		</>
	);
};

export default PaymentSteps;
